import ReactDOM from "react-dom";
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState, ReactElement} from 'react';
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import {useAppState, useRootStore} from "@stores/AppStateContext";

import {MenuAlignment, MenuPlacement, normalizePosition} from "../../../utils/normalizePosition";
import {Pointer, PointerPosition} from "../../components/Pointer/Pointer";

import {editorContextRootRef} from '../EditorReact';
import {leftMenuContextRootRef} from '../ListView';
import {screenSizeProvider} from "@stores/ScreenSizeProvider";


import './ctx-menu-position.css';


export interface ContextMenuParams {
    [x: string]: any;
}

export interface ContextMenuAdditionalParamsType {
    [x: string]: any;
}

export type MousePositionParams = {
    clientX: number,
    clientY: number,
} | {
    getTargetBoundingBox: () => DOMRect;
}

export interface ContextMenuProps {
    type: ContextMenuType,
    id: string,
    clientX?: number,
    clientY?: number,
    isDark?: boolean,
    params?: ContextMenuParams,
    onClose?: () => void,
    menuPlacement?: MenuPlacement;
    menuAlignment?: MenuAlignment;
    withPointer?: boolean;
    isPointerLarge?: boolean;
    getTargetBoundingBox?: () => DOMRect | undefined;
    hotKeys?: Record<string, () => void>;
    VERTICAL_SHIFT?: number,
    HORIZONTAL_SHIFT?: number,
    minWidth?: number,
    maxWidth?: number,
    withOverlay?: boolean,
    shouldForcePlace?: boolean,
}

export type ContextMenuType =
    'item'
    | 'task'
    | 'avatar'
    | 'document'
    | 'workspace'
    | 'workspace-row'
    | 'add-to-cluster'
    | 'editor-text'
    | 'editor-trigger'
    | 'editor-table'
    | 'editor-link'
    | 'editor-image'
    | 'editor-plus'
    | 'editor-embed'
    | 'editor-callout'
    | 'notification-centre'
    | 'select-options'
    | 'calendar-select-options'
    | 'calendar-select-repeat-options'
    | 'quiz'
    | 'quiz-question'
    | 'slide'
    | 'acknowledgments-overview'
    | 'avatar-group-users'
    | 'taskList'
    | 'taskListUnit'
    | 'taskListUnit-select-options'
    | 'taskList-schedule'
    | 'tree-quick-actions'
    | 'profile'

const getElement = (id: string) => window.document.getElementById(id);

const editorContextsTriggeredWithoutRightClickEvent = new Set([
    'editor-text',
    'editor-trigger',
    'editor-link',
]);

const editorContexts = new Set([
    ...editorContextsTriggeredWithoutRightClickEvent,
    'editor-table',
    'editor-plus',
    'editor-image',
    'editor-embed',
    'editor-callout',
]);

export const getRenderingScope = (type: ContextMenuType, params: any): {
    anchorScope: HTMLElement | null;
    popupScope: HTMLElement | null;
} => {
    const middle = getElement('contextMenuMiddle');
    const normal = getElement('contextMenu');
    const low = getElement('contextMenuLow');

    switch (type) {
        case 'editor-text':
        case 'editor-trigger':
        case 'editor-plus':
        case 'editor-link':
        case 'editor-table':
        case 'editor-image':
        case 'add-to-cluster':
        case 'document':
            return {
                anchorScope: editorContextRootRef.current,
                popupScope: middle || normal,
            };
        case 'item':
            if (params.inEditorView === true) {
                return {
                    anchorScope: editorContextRootRef.current,
                    popupScope: middle || normal,
                };
            } else {
                return {
                    anchorScope: leftMenuContextRootRef.current,
                    popupScope: middle || normal,
                };
            }
        case 'taskList-schedule':
            return {
                anchorScope: low || normal,
                popupScope: low || normal,
            };
        case 'quiz':
        case 'quiz-question':
        default:
            return {
                anchorScope: normal,
                popupScope: normal,
            };
    }
}

/* if context menu opens automatically on click in editor we need a catch-up to avid closing it on the same click handle */
export const isTriggeredBySelectionChangeInEditor = (contextMenu: ContextMenuProps | null) =>
    contextMenu !== null
    && editorContextsTriggeredWithoutRightClickEvent.has(contextMenu.type);
export const isEditorContext = (contextMenu: ContextMenuProps | null) => contextMenu !== null && editorContexts.has(contextMenu.type);


export interface MenuPosition {
    params: any,
    actualDirection: MenuPlacement,
    style: {
        top?: string;
        left?: string;
    },
    pointer?: PointerPosition;
    pointerOffset?: { x: number, y: number };
}

type RenderFunction = (type: ContextMenuType, params: any, handleClose: any, isDark?: boolean) => ReactElement | null;

export interface ContextMenuManagerProps {
    renderContextMenu: RenderFunction;
    renderScope?: HTMLElement;
}

const ContextMenuManagerBase = ({renderContextMenu, renderScope}: ContextMenuManagerProps) => {
    const appState = useAppState();

    const isClickInsideRef = useRef<boolean>(false);

    const isInContextMenuClick = (clickedTarget: HTMLElement) => {
        return !!(
            clickedTarget.closest('.ctx-menu-position')
            || (isTriggeredBySelectionChangeInEditor(appState.contextMenu) && clickedTarget.closest('.ER-wrapper'))
        )
    }

    const handleClose = useCallback(() => {
        if (appState.contextMenu === null) {
            return;
        }
        appState.setContextMenu(null);
    }, []);

    const onDocumentMouseDown = useCallback((event: MouseEvent) => {
        const clickedTarget: HTMLElement = event.target as HTMLElement;

        isClickInsideRef.current = isInContextMenuClick(clickedTarget);
    }, [isClickInsideRef]);

    const onDocumentClick = useCallback((event: MouseEvent): void => {
        if (event.defaultPrevented) {
            return;
        }
        const clickedTarget: HTMLElement = event.target as HTMLElement;

        if (isClickInsideRef.current || isInContextMenuClick(clickedTarget)) {
            return;
        }

        handleClose();
    }, [handleClose, isClickInsideRef]);

    useEffect(() => {
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('mousedown', onDocumentMouseDown);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('mousedown', onDocumentMouseDown);
        }
    }, [onDocumentClick, onDocumentMouseDown]);

    const stateCtxMenu = appState.contextMenu;
    const menus: ContextMenuProps[] = [];
    if (stateCtxMenu) menus.push(stateCtxMenu);
    menus.push(...appState.additionalContextMenus);

    return (
        <>
            {menus.map((menu, idx) => {
                const contentFor = (m: ContextMenuProps) =>
                    renderContextMenu(
                        m.type,
                        m.params,
                        m === stateCtxMenu ? handleClose : appState.removeAdditionalContextMenu,
                        m.isDark
                    );

                const content = contentFor(menu);
                if (!content) return null;

                // Determine scopes
                let scopes;
                if (renderScope) {
                    scopes = { anchorScope: renderScope, popupScope: renderScope };
                } else {
                    scopes = getRenderingScope(menu.type, menu.params);
                }

                if (!scopes.anchorScope || !scopes.popupScope) return null;

                return (
                    <CtxMenu
                        key={menu.id}
                        anchorScope={scopes.anchorScope}
                        popupScope={scopes.popupScope}
                        ctxMenu={menu}
                        content={content}
                        layerIndex={idx}
                    />
                );
            })}
        </>
    )
}

export const ContextMenuManager = observer(ContextMenuManagerBase);

export interface CtxMenuProps {
    ctxMenu: ContextMenuProps;
    content: ReactElement;
    anchorScope: HTMLElement;
    popupScope: HTMLElement;
    layerIndex?: number;
}

const Z_STEP = 50;
const Z_MENU_BASE = 4500;
const Z_MIDDLE_BASE = 950;

export const CtxMenu = (
    {
        ctxMenu,
        content,
        anchorScope,
        popupScope,
        layerIndex = 0,
    }: CtxMenuProps
) => {
    const appState= useAppState();
    const rootStore = useRootStore();

    const [menuPosition, setMenuPosition] = useState<MenuPosition | undefined>(undefined);
    const {
        type,
        params,
        isDark = false,
        isPointerLarge = false,
        minWidth,
        maxWidth,
        shouldForcePlace = false,
        withOverlay = false,
    } = ctxMenu || {};

    const isMiddleLayer = popupScope.id === 'contextMenuMiddle';
    const baseZ = isMiddleLayer ? Z_MIDDLE_BASE : Z_MENU_BASE;
    const zMenu = baseZ + layerIndex * Z_STEP;
    const zOverlay = zMenu - 10;

    const prevWidthRef = useRef<number>(0);
    const [recalculationCount, forceUpdate] = useState<number>(0);
    
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const popupRef = useRef<HTMLDivElement | null>(null);
    const pointerRef = useRef<HTMLDivElement | null>(null);
    const [isSynced, setIsSynced] = useState(false);

    const resizeObserverCallback: ResizeObserverCallback = (entries) => {
        const size = Array.isArray(entries[0].contentBoxSize)
            ? entries[0].contentBoxSize[0].inlineSize
            : (entries[0].contentBoxSize as any)?.inlineSize ?? entries[0].contentRect.width;

        if (size !== prevWidthRef.current) {
            prevWidthRef.current = size;
            forceUpdate(c => c + 1);
        }
    };

    useEffect(() => {
        if (!ctxMenu || !popupRef.current) return;
        const ro = new ResizeObserver(resizeObserverCallback);
        ro.observe(popupRef.current);
        return () => ro.disconnect();
    }, [ctxMenu, type, params]);

    useLayoutEffect(
        () => {
            const {
                clientX, clientY, withPointer,
                menuPlacement, menuAlignment, getTargetBoundingBox,
                VERTICAL_SHIFT, HORIZONTAL_SHIFT, type, isPointerLarge
            } = ctxMenu || {};
            const isMobile = screenSizeProvider.view !== 'desktop';
            if (isMobile && !shouldForcePlace) {
                return;
            }
            if (!content || !anchorScope) {
                return;
            }

            if (
                (getTargetBoundingBox !== undefined || (clientX !== undefined && clientY !== undefined))
            ) {
                const targetBoundingBox = getTargetBoundingBox ? getTargetBoundingBox() : undefined;
                
                const newMenuPosition = normalizePosition({
                    mouseX: clientX,
                    mouseY: clientY,
                    // Use actual popup if measured, otherwise a safe fallback size
                    contextMenu: (popupRef.current && popupRef.current.clientWidth > 0) 
                        ? popupRef.current 
                        : { clientWidth: 200, clientHeight: 200 } as any,
                    withPointer: withPointer,
                    placement: menuPlacement,
                    alignment: menuAlignment,
                    targetBoundingBox: targetBoundingBox,
                    VERTICAL_SHIFT: VERTICAL_SHIFT,
                    HORIZONTAL_SHIFT: HORIZONTAL_SHIFT,
                    getScopeBoundingClientRect: anchorScope.getBoundingClientRect.bind(anchorScope),
                    isPointerLarge: isPointerLarge,
                    scopeAdditionalPadding: 0,
                });
                
                let {pointerPosition} = newMenuPosition;
                let pointerOffset = undefined;

                if (pointerPosition) {
                    pointerOffset = {
                        x: pointerPosition.left - newMenuPosition.left,
                        y: pointerPosition.top - newMenuPosition.top,
                    };
                }

                setMenuPosition({
                    params: params!,
                    actualDirection: newMenuPosition.actualDirection,
                    style: {
                        top: `${newMenuPosition.top}px`,
                        left: `${newMenuPosition.left}px`,
                    },
                    pointer: pointerPosition,
                    pointerOffset
                });
                setIsSynced(false);
            }

            return;
        }, [
            recalculationCount,
            menuPosition?.params,
            params,
            anchorRef,
            popupRef,
            ctxMenu,
            ctxMenu?.type,
            ctxMenu?.getTargetBoundingBox,
            rootStore.leftMenuWidth,
            anchorScope,
        ]
    );

    // Sync Loop Hook
    useLayoutEffect(() => {
        if (!anchorRef.current || !popupRef.current || !menuPosition) return;

        const anchor = anchorRef.current;
        const pointer = pointerRef.current;
        
        let frameId: number;
        const sync = () => {
            if (!anchorRef.current || !popupRef.current) return;
            const anchorRect = anchorRef.current.getBoundingClientRect();
            
            // Sync Menu
            popupRef.current.style.top = `${anchorRect.top}px`;
            popupRef.current.style.left = `${anchorRect.left}px`;

            // Sync Pointer
            if (pointer && menuPosition.pointerOffset) {
                pointer.style.top = `${anchorRect.top + menuPosition.pointerOffset.y}px`;
                pointer.style.left = `${anchorRect.left + menuPosition.pointerOffset.x}px`;
            }
            
            setIsSynced(true);
            frameId = requestAnimationFrame(sync);
        };
        sync();
        return () => cancelAnimationFrame(frameId);
    }, [menuPosition, ctxMenu.id, anchorScope]);

    const isMobile = screenSizeProvider.view !== 'desktop';
    const isVisible = !!menuPosition && isSynced;

    return (
        <>
            {ReactDOM.createPortal(
                <div
                    ref={anchorRef}
                    className="ctx-anchor"
                    style={{
                        ...menuPosition?.style,
                        opacity: 0,
                        visibility: 'visible',
                    }}
                />,
                anchorScope
            )}
            {ReactDOM.createPortal(
                <>
                    {withOverlay && (
                        <div
                            key={`${ctxMenu.type}#${ctxMenu.id}overlay`}
                            className="ctx-menu-overlay"
                            style={{zIndex: zOverlay}}
                        />
                    )}
                    <div className={classNames({
                        'ctx-menu-position': true,
                        "ctx-menu-position-mobile": shouldForcePlace
                    })}
                         key={`${ctxMenu.type}#${ctxMenu.id}`}
                         ref={popupRef}
                         style={{
                             zIndex: zMenu,
                             minWidth: isMobile && !shouldForcePlace ? undefined : minWidth,
                             maxWidth: isMobile && !shouldForcePlace ? undefined : maxWidth,
                             position: 'fixed',
                             visibility: isVisible ? 'visible' : 'hidden',
                         }}
                         onClick={(event) => {
                             if (appState.latestAdditionalCtxMenu && appState.latestAdditionalCtxMenu!.id !== ctxMenu.id) {
                                 appState.removeAdditionalContextMenu();
                             }
                         }}
                    >
                        {content}
                    </div>
                    {menuPosition?.pointer ?
                        <div ref={pointerRef} style={{
                            position: 'fixed', 
                            zIndex: zMenu + 1,
                            visibility: isVisible ? 'visible' : 'hidden',
                        }}>
                            <Pointer isDark={isDark} isLarge={isPointerLarge} {...menuPosition.pointer} top={0} left={0}/>
                        </div>
                        : null}
                </>,
                popupScope
            )}
        </>
    );
}
